import { Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule, FormControl } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MaterialModule } from '@app/material.module';
import { StatusDefinition } from '@app/types/status.type';
import { StatusTranslatorService } from '@app/services/utils/status-translator.service';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { MatChipInputEvent } from '@angular/material/chips';

@Component({
    selector: 'app-status-dialog',
    standalone: true,
    imports: [CommonModule, MaterialModule, ReactiveFormsModule],
    templateUrl: './status-dialog.component.html',
    styleUrls: ['./status-dialog.component.scss']
})
export class StatusDialogComponent implements OnInit {
    private fb = inject(FormBuilder);
    private dialogRef = inject(MatDialogRef<StatusDialogComponent>);
    private statusTranslator = inject(StatusTranslatorService);
    public data: StatusDefinition | null = inject(MAT_DIALOG_DATA);

    statusForm!: FormGroup;
    isEditMode = false;
    readonly separatorKeysCodes = [ENTER, COMMA] as const;

    ngOnInit(): void {
        this.isEditMode = !!this.data;

        this.statusForm = this.fb.group({
            status: [
                { value: this.data?.status || '', disabled: this.isEditMode },
                [Validators.required, Validators.minLength(1)]
            ],
            nombreSurtimiento: [this.data?.nombreSurtimiento || '', [Validators.required, Validators.minLength(1)]],
            nombreCobranza: [this.data?.nombreCobranza || ''],
            description: [this.data?.description || '', [Validators.required]],
            order: [this.data?.order || 1, [Validators.required, Validators.min(0)]],
            variations: [this.data?.variations || []],
            color: [this.data?.color || ''],
            dashboardGroup: [this.data?.dashboardGroup || '']
        });
    }

    /** Grupos de dashboard existentes en el catálogo (para el datalist de sugerencias) */
    get existingDashboardGroups(): string[] {
        const groups = new Set<string>();
        for (const s of this.statusTranslator.getStatuses()()) {
            if (s.dashboardGroup && !s.deleted) groups.add(s.dashboardGroup);
        }
        return Array.from(groups);
    }

    get variations(): string[] {
        return this.statusForm.get('variations')?.value || [];
    }

    addVariation(event: MatChipInputEvent): void {
        const value = (event.value || '').trim();
        if (value) {
            const currentVariations = this.variations;
            this.statusForm.patchValue({
                variations: [...currentVariations, value]
            });
        }
        event.chipInput!.clear();
    }

    removeVariation(variation: string): void {
        const currentVariations = this.variations;
        const index = currentVariations.indexOf(variation);
        if (index >= 0) {
            currentVariations.splice(index, 1);
            this.statusForm.patchValue({
                variations: [...currentVariations]
            });
        }
    }

    onCancel(): void {
        this.dialogRef.close();
    }

    onSave(): void {
        if (this.statusForm.valid) {
            const formValue = this.statusForm.getRawValue(); // getRawValue to include disabled fields
            this.dialogRef.close(formValue);
        }
    }
}
